// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Heading.tsx
 * @input Uses React, HTMLAttributes, ReactNode
 * @output Exports Heading component, HeadingProps, HeadingLevel types
 * @position Core implementation; lives in own Heading/ dir, re-exported by Text/
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Text/Text.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Heading/Heading.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Text/index.ts (exports if types change)
 * - /apps/storybook/stories/Text.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Heading/ (showcase blocks)
 * - /packages/cli/assets/templates/blocks/components/Text/ (showcase blocks)
 */
import { lazy, Suspense, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { colorStyles, sizeByLevelStyles, sizeByTypeStyles, defaultWeightByTypeStyles, displayStyles, justifyStyles, truncationStyles, wordBreakStyles, textWrapStyles, capsizeStyles, decorationStyles, truncationTooltipStyles } from "../Text/text.stylex.js";
import { useTruncation } from "../Text/useTruncation.js";
import { resolveStyleColor } from "../Text/Text.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const LazyXDSTooltip = /*#__PURE__*/lazy(async () => import("../Tooltip/Tooltip.js").then(mod => ({
  default: mod.Tooltip
})));

/**
 * Heading level (1-6). Determines both visual styling and semantic HTML element.
 */

/**
 * Display type variants for headings. Applies display-scale sizing
 * (larger, lighter) while preserving the semantic heading element.
 */

const levelToTag = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6'
};

/**
 * Heading - Semantic heading component
 *
 * Renders headings with semantic HTML (h1-h6) and themed styling.
 *
 * @example
 * ```
 * <Heading level={1}>Page Title</Heading>
 * <Heading level={2}>Section</Heading>
 * <Heading level={2} accessibilityLevel={3}>Sidebar Section</Heading>
 * <Heading level={1} type="display-1">Hero Title</Heading>
 * <Heading level={2} type="display-2">$1.2M Revenue</Heading>
 * <Heading level={2} maxLines={1}>Very Long Section Title...</Heading>
 * <Heading level={3} color="secondary">Muted Heading</Heading>
 * ```
 */
export function Heading({
  level,
  type,
  accessibilityLevel,
  color = 'primary',
  display = 'block',
  maxLines = 0,
  hasTruncateTooltip = true,
  wordBreak,
  textWrap,
  justify = 'start',
  hasCapsize = false,
  hasStrikethrough = false,
  xstyle,
  className,
  style,
  children,
  ref,
  ...props
}) {
  const Component = levelToTag[level];

  // If accessibilityLevel differs from visual level, use aria-level
  const ariaProps = accessibilityLevel && accessibilityLevel !== level ? {
    'aria-level': accessibilityLevel
  } : {};

  // Resolve wordBreak with smart default
  const resolvedWordBreak = wordBreak ?? (maxLines === 1 ? 'break-all' : 'break-word');

  // Resolve display - force block when maxLines > 0 or hasCapsize
  const resolvedDisplay = maxLines > 0 || hasCapsize ? 'block' : display;

  // Truncation detection
  const truncation = useTruncation({
    maxLines
  });

  // Tooltip for truncated text
  const tooltipPlacement = typeof hasTruncateTooltip === 'string' ? hasTruncateTooltip : 'above';
  const tooltipEnabled = maxLines > 0 && hasTruncateTooltip !== false && truncation.isTruncated;

  // Ref for the heading element (used as tooltip anchor)
  const headingRef = useRef(null);

  // Build inline style for -webkit-line-clamp (dynamic value)
  const inlineStyle = maxLines > 1 ? {
    WebkitLineClamp: maxLines
  } : undefined;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Component, {
      ref: mergeRefs(ref, truncation.ref, headingRef),
      ...mergeProps(themeProps('heading', {
        level,
        color,
        ...(type && {
          type
        })
      }), stylex.props(colorStyles[resolveStyleColor(color)], type ? sizeByTypeStyles[type] : sizeByLevelStyles[level], type && defaultWeightByTypeStyles[type],
      // Display: use truncation styles when maxLines > 0
      maxLines === 1 ? truncationStyles.singleLine : maxLines > 1 ? truncationStyles.multiLine : displayStyles[resolvedDisplay],
      // Word break when truncating
      maxLines > 0 && wordBreakStyles[resolvedWordBreak],
      // Text wrap
      textWrap && textWrapStyles[textWrap],
      // Justify (text alignment)
      justify !== 'start' && justifyStyles[justify],
      // Capsize
      hasCapsize && capsizeStyles.enabled,
      // Decorations
      hasStrikethrough && decorationStyles.strikethrough,
      // User xstyle
      xstyle), className, {
        ...style,
        ...inlineStyle
      }),
      title: tooltipEnabled ? truncation.fullText : undefined,
      ...ariaProps,
      ...props,
      children: children
    }), tooltipEnabled && /*#__PURE__*/_jsx(Suspense, {
      fallback: null,
      children: /*#__PURE__*/_jsx(LazyXDSTooltip, {
        anchorRef: headingRef,
        content: /*#__PURE__*/_jsx("span", {
          ...stylex.props(truncationTooltipStyles.content),
          children: truncation.fullText
        }),
        placement: tooltipPlacement
      })
    })]
  });
}
Heading.displayName = 'Heading';